import React, { Component } from 'react';
import * as am4core from '@amcharts/amcharts4/core';
import * as am4charts from '@amcharts/amcharts4/charts';
import am4themesAnimated from '@amcharts/amcharts4/themes/animated';
import _ from 'lodash';
import * as MD from '@material-ui/core';
import { History } from 'history';
import { ILocaleInfos, I18N } from '../../Store/I18n/Types';
import am4lang_en_US from "@amcharts/amcharts4/lang/en_US";
import am4lang_fr_FR from "@amcharts/amcharts4/lang/fr_FR";
import { IStatsState } from '../../Store/Statistics/Types';
import { IApplicationState } from '../../Store';
import { Dispatch } from 'redux';
import { connect } from 'react-redux';
import { convertColor } from '../../Utils/Various';

const styles = (theme:any) => MD.createStyles({
    root: {
      width: '100%',
    },
    paper: {
      width: '100%',
      clear:'both',
      marginBottom: theme.spacing(2),
    },
  });

  interface IPropsFromState {
    i18n: I18N;
    localeInfos: ILocaleInfos;
    stats: IStatsState;
}
  
interface IPropsFromDispatch {
}
  
interface IOwnProps {
    chartId: string,
    data: any[],
    category: string,
    year: number,
    value: string,
    history: History,
}

interface ILineState {
    showPercent: boolean,
    showRepReg: boolean,
    hideLegend: boolean
}
const initialState: ILineState = {
    showPercent: false,
    showRepReg: true,
    hideLegend: false
}

type AllProps =
    MD.WithTheme &
    MD.WithStyles<typeof styles> &
    IPropsFromState &
    IPropsFromDispatch &
    IOwnProps;


export class LineChartComponent extends Component<AllProps, ILineState> {

    chart: any;
    
    public constructor(props: AllProps) {
        super(props);
        this.state = {...initialState};
    }

    componentDidMount() {
        this.initChart(this.props);
    }

    componentDidUpdate(prevProps: AllProps, prevState:ILineState) {
        //Handle refreshing the chart when the dataset changes
        if (!_.isEqual(prevProps.data, this.props.data) || !_.isEqual(prevProps.localeInfos.momentLocale, this.props.localeInfos.momentLocale)) {
            if (this.chart) {
                this.chart.dispose();
            }
            this.initChart(this.props);
        }
        if (!_.isEqual(prevState.showPercent, this.state.showPercent)) {
            this.chart.dispose();
            this.initChart(this.props);
        }
        if (!_.isEqual(prevProps.theme, this.props.theme)) {
            this.chart.dispose();
            this.initChart(this.props, this.state);
        }
    }

    componentWillUnmount() {
        if (this.chart) {
            this.chart.dispose();
        }
    }

    processOver(hoveredSeries:any, chart:any) {
        hoveredSeries.toFront();
      
        hoveredSeries.segments.each(function(segment:any) {
          segment.setState("hover");
        })
      
        chart.series.each(function(series:any) {
          if (series !== hoveredSeries) {
            series.segments.each(function(segment:any) {
              segment.setState("dimmed");
            })
            series.bulletsContainer.setState("dimmed");
          }
        });
        return chart
    }
      
    processOut(hoveredSeries:any, chart: any) {
        chart.series.each(function(series:any) {
            series.segments.each(function(segment:any) {
            segment.setState("default");
            })
            series.bulletsContainer.setState("default");
        });
    }
    
// Create series
    private createSeries(groupID:number, field:any, newColor:any, chart:any) {
        var series = chart.series.push(new am4charts.LineSeries());
        let color = convertColor(newColor)
        series.dataFields.valueY = field;
        series.dataFields.categoryX = this.props.category;
        series.name = field;
        series.tooltipText = "{categoryX}: [b]{valueY}[/]";
        series.strokeWidth = 2;
        series.stroke = am4core.color(color);
        series.fill = am4core.color(color);
        
        // Set up tooltip
        let tt:any = series.tooltip
        tt.getFillFromObject = false;
        series.tooltipText = `% {categoryX}: {valueY}%`;
        series.adapter.add("tooltipText", function(tooltipText:string) {
            if (series.tooltipDataItem.dataContext) {
                let data:any = series.tooltipDataItem.dataContext
                let dataGroupId:any = data[groupID]
                tt.label.fill = am4core.color("black");
                tt.background.stroke = am4core.color(color);
                tt.background.strokeWidth = 2;
                if (dataGroupId !== undefined) {
                    return(
                        `${field}
                        Tps.: ${dataGroupId.duration_hms}
                        ${tooltipText}`
                    )
                }
            }
            return tooltipText;
        })
        
        var bullet = series.bullets.push(new am4charts.CircleBullet());
        bullet.circle.stroke = am4core.color("#fff");
        bullet.circle.strokeWidth = 2;
        
        return series;
    }

    initChart(props:AllProps, state?:ILineState) {
        //All of our work goes here now
        const { chartId, category, data, i18n, localeInfos, theme, stats } = this.props; 
        am4core.useTheme(am4themesAnimated);

        // Create chart instance
        let chart = am4core.create(chartId, am4charts.XYChart);
        if (localeInfos.momentLocale === "en-us"){
            chart.language.locale = am4lang_en_US;
        } else if (localeInfos.momentLocale === "fr") {
            chart.language.locale = am4lang_fr_FR
        }
        // Add data
        chart.data = data;
        chart.logo.hidden = true
        chart.logo.height = -15000
        // Create axes
        let xAxis:any = chart.xAxes;
        xAxis = chart.xAxes.push(new am4charts.CategoryAxis() as any);
        xAxis.dataFields.category = category; //categoryLabel=mediaName
        // Style xAxis
        xAxis.renderer.grid.template.location = 0;
        xAxis.renderer.labels.template.fill = am4core.color(theme.palette.text.primary);
        xAxis.renderer.labels.template.fontSize = '12px';
        xAxis.renderer.minGridDistance =  5;
        xAxis.events.on("sizechanged", function(ev:any) {
            xAxis.renderer.labels.template.rotation = -45;
            xAxis.renderer.labels.template.horizontalCenter = "right";
            xAxis.renderer.labels.template.verticalCenter = "middle";
        });
        xAxis.title.text = `${i18n._("Year")} ${props.year}`;
        xAxis.title.fill = am4core.color("#00b0ff");
        // Create y axes value
        let yAxis = chart.yAxes.push(new am4charts.ValueAxis());
        yAxis.title.text = i18n._("% OF SPEAKING TIME");
        //yAxis.title.fontSize = "20px";
        yAxis.title.fill = am4core.color("#00b0ff");
        yAxis.renderer.labels.template.fill = am4core.color(theme.palette.text.primary);
        // Adapter adds custom text to the label
        yAxis.renderer.labels.template.adapter.add("text", (label, target) => {
            return label + "%";
        });
        yAxis.renderer.minGridDistance = 50;
        yAxis.min = 0
        yAxis.strictMinMax = true
        
        let datax = props.data
        if (stats.statsEvolutionFilters !== null && stats.statsEvolutionFilters.political_group_ids !== null ) {
            stats.statsEvolutionFilters.political_group_ids.map(id => {
                let validatedData = datax.find( x => x[id] !== undefined)
                if (validatedData !== undefined) {
                    let serie = validatedData[id]
                    return this.createSeries(id, serie["libelle"], serie["color"], chart);    
                } else return null
                })
        }

        /* chart legend */
        chart.legend = new am4charts.Legend();
        var legendContainer = am4core.create("legenddiv", am4core.Container);
        legendContainer.logo.hidden = true
        legendContainer.logo.height = -15000
        legendContainer.width = am4core.percent(100);
        legendContainer.height = am4core.percent(100);
        chart.legend.parent = legendContainer;
        chart.legend.useDefaultMarker = true;
        chart.legend.labels.template.fill = am4core.color(theme.palette.text.primary);
        chart.legend.valueLabels.template.fill = am4core.color(theme.palette.text.primary); 
        chart.legend.scrollable = true;

        var dropShadow = new am4core.DropShadowFilter();
        dropShadow.dy = 1;
        dropShadow.dx = 1;
        dropShadow.opacity = 0.5;
                
        // Make up for the top x axis renderer not actually occupying any space
        chart.topAxesContainer.paddingTop = 30;

        // Make a panning cursor
        chart.cursor = new am4charts.XYCursor();
        // Create vertical scrollbar and place it before the value axis
        chart.scrollbarY = new am4core.Scrollbar();
        chart.scrollbarY.parent = chart.leftAxesContainer;
        chart.scrollbarY.toBack();

        // Create a horizontal scrollbar with previe and place it underneath the date axis
        let scrollbarX = new am4core.Scrollbar();
        chart.scrollbarX = scrollbarX;
        chart.scrollbarX.parent = chart.bottomAxesContainer;

        this.chart = chart;
    }

    render() {
        return (
            <>
            <div id={this.props.chartId} style={{ width: "100%", height: "500px" }} />
            <div id="legenddiv" style={{border: "2px dotted #C4C4C4", margin: "5px 0 20px 0"}}></div>
            </>
        )
    }
}
const mapStateToProps = ( { i18n, ui, stats } : IApplicationState) => ({
    localeInfos: i18n.localeInfos,
    i18n: i18n.i18n,
    ui,
    stats
  });
  
  const mapDispatchToProps = (dispatch: Dispatch) => ({
  });
  
  const LineChart = connect( mapStateToProps, mapDispatchToProps)
  (MD.withTheme(MD.withStyles(styles)(LineChartComponent)));
  
  export { LineChart };